import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { DocumentationItem, DocumentationCategory, SearchResult } from '@/types/documentation'
import { loadDocumentationFromFiles } from '@/utils/markdownLoader'

export const useDocumentationStore = defineStore('documentation', () => {
  // State
  const documentation = ref<DocumentationCategory[]>([])
  const searchQuery = ref('')
  const searchResults = ref<SearchResult[]>([])
  const isLoading = ref(false)
  const currentCategory = ref<string>('')
  const currentSection = ref<string>('')

  // Getters
  const getCategoryBySlug = computed(() => {
    return (slug: string) => documentation.value.find(cat => cat.slug === slug)
  })

  const getSectionBySlug = computed(() => {
    return (categorySlug: string, sectionSlug: string) => {
      const category = getCategoryBySlug.value(categorySlug)
      return category?.sections.find(section => section.slug === sectionSlug)
    }
  })

  const getAllItems = computed(() => {
    const items: DocumentationItem[] = []
    documentation.value.forEach(category => {
      category.sections.forEach(section => {
        items.push({
          id: `${category.slug}-${section.slug}`,
          title: section.title,
          content: section.content,
          category: category.title,
          categorySlug: category.slug,
          sectionSlug: section.slug,
          tags: section.tags || [],
          lastUpdated: section.lastUpdated,
        })
      })
    })
    return items
  })

  // Actions
  const setDocumentation = (docs: DocumentationCategory[]) => {
    documentation.value = docs
  }

  const setCurrentCategory = (category: string) => {
    currentCategory.value = category
  }

  const setCurrentSection = (section: string) => {
    currentSection.value = section
  }

  const searchDocumentation = async (query: string) => {
    if (!query.trim()) {
      searchResults.value = []
      return
    }

    isLoading.value = true
    searchQuery.value = query

    try {
      // Simulate search delay
      await new Promise(resolve => setTimeout(resolve, 300))

      const results: SearchResult[] = []
      const searchTerm = query.toLowerCase()

      getAllItems.value.forEach(item => {
        const titleMatch = item.title.toLowerCase().includes(searchTerm)
        const contentMatch = item.content.toLowerCase().includes(searchTerm)
        const tagMatch = item.tags.some(tag => tag.toLowerCase().includes(searchTerm))

        if (titleMatch || contentMatch || tagMatch) {
          let score = 0
          if (titleMatch) score += 3
          if (contentMatch) score += 1
          if (tagMatch) score += 2

          results.push({
            ...item,
            score,
            matches: {
              title: titleMatch,
              content: contentMatch,
              tags: tagMatch,
            },
          })
        }
      })

      // Sort by relevance score
      searchResults.value = results.sort((a, b) => b.score - a.score)
    } catch (error) {
      console.error('Search error:', error)
      searchResults.value = []
    } finally {
      isLoading.value = false
    }
  }

  const clearSearch = () => {
    searchQuery.value = ''
    searchResults.value = []
  }

  const loadDocumentationFromMarkdown = async () => {
    try {
      const docs = await loadDocumentationFromFiles()
      
      if (docs && docs.length > 0) {
        setDocumentation(docs)
      } else {
        setFallbackDocumentation()
      }
    } catch (error) {
      console.error('Documentation store: Failed to load documentation from markdown:', error)
      setFallbackDocumentation()
    }
  }

  const setFallbackDocumentation = () => {
    // Set empty documentation to prevent errors
    setDocumentation([])
  }

  const getBreadcrumbs = computed(() => {
    const breadcrumbs: Array<{ label: string; path: string; active: boolean }> = [
      { label: 'Home', path: '/', active: false },
    ]

    if (currentCategory.value) {
      const category = getCategoryBySlug.value(currentCategory.value)
      if (category) {
        breadcrumbs.push({
          label: category.title,
          path: `/docs/${currentCategory.value}`,
          active: !currentSection.value,
        })
      }
    }

    if (currentSection.value) {
      const section = getSectionBySlug.value(currentCategory.value, currentSection.value)
      if (section) {
        breadcrumbs.push({
          label: section.title,
          path: `/docs/${currentCategory.value}/${currentSection.value}`,
          active: true,
        })
      }
    }

    return breadcrumbs
  })

  return {
    // State
    documentation,
    searchQuery,
    searchResults,
    isLoading,
    currentCategory,
    currentSection,

    // Getters
    getCategoryBySlug,
    getSectionBySlug,
    getAllItems,
    getBreadcrumbs,

    // Actions
    setDocumentation,
    setCurrentCategory,
    setCurrentSection,
    searchDocumentation,
    clearSearch,
    loadDocumentationFromMarkdown,
  }
})
